import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useBrgList } from '../../hooks/useBrgList';
import { useEngagementSummary } from '../../hooks/useEngagementSummary';
import { useDeleteBrg } from '../../hooks/useBrgMutations';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { DeleteBrgDialog } from '../../components/brg/DeleteBrgDialog';
import { BrgListItemDto } from '../../types';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) {
    return '$0';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function renderStatusBadge(status: BrgListItemDto['status']): React.ReactNode {
  const isActive = status === 'Active';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isActive ? '#4caf50' : '#9e9e9e',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: isActive ? '#2e7d32' : '#757575' }}>
        {status}
      </span>
    </div>
  );
}

export function AdminLandingPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth.roles.includes('BRG.Admin');
  const brgQuery = useBrgList();
  const engagementQuery = useEngagementSummary();
  const [selectedBrgIds, setSelectedBrgIds] = useState<number[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteMutation = useDeleteBrg();

  const isLoading = brgQuery.isLoading || engagementQuery.isLoading;
  const hasError = brgQuery.isError || engagementQuery.isError;
  const errorMessage =
    brgQuery.error?.message ?? engagementQuery.error?.message ?? 'Failed to load data.';

  const sortedBrgs = useMemo(() => {
    if (!brgQuery.data) return [];
    return [...brgQuery.data].sort((a, b) => a.name.localeCompare(b.name));
  }, [brgQuery.data]);

  function toggleSelect(id: number) {
    setSelectedBrgIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const canCreateNew = selectedBrgIds.length === 0;
  const canEdit = selectedBrgIds.length === 1;
  const canDelete = selectedBrgIds.length === 1;
  const selectedBrg = sortedBrgs.find((b) => b.brgId === selectedBrgIds[0]);

  function handleEdit() {
    if (canEdit) {
      void navigate(`/admin/brgs/${selectedBrgIds[0]?.toString()}/edit`);
    }
  }

  function handleNewBrg() {
    void navigate('/admin/brgs/new');
  }

  function handleMembers() {
    void navigate('/admin/members');
  }

  function handleMasters() {
    void navigate('/admin/masters');
  }

  function handleDeleteConfirm() {
    if (!selectedBrg) return;
    deleteMutation.mutate(selectedBrg.brgId.toString(), {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setSelectedBrgIds([]);
      },
    });
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading BRG data..." fullPage />;
  }

  if (hasError) {
    return (
      <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
        <ErrorMessage
          message={errorMessage}
          onRetry={() => {
            void brgQuery.refetch();
            void engagementQuery.refetch();
          }}
        />
      </div>
    );
  }

  const engagement = engagementQuery.data;

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>

      {/* Action Bar */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {isAdmin && (
        <button
          onClick={handleNewBrg}
          disabled={!canCreateNew}
          style={{
            padding: '9px 20px',
            backgroundColor: canCreateNew ? '#0078d4' : '#cccccc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: canCreateNew ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          NEW BRG
        </button>
        )}

        {isAdmin && (
        <button
          onClick={handleEdit}
          disabled={!canEdit}
          style={{
            padding: '9px 20px',
            backgroundColor: canEdit ? '#0078d4' : '#cccccc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: canEdit ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          EDIT BRG
        </button>
        )}

        {isAdmin && (
        <button
          onClick={() => setDeleteDialogOpen(true)}
          disabled={!canDelete}
          style={{
            padding: '9px 20px',
            backgroundColor: canDelete ? '#d32f2f' : '#cccccc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: canDelete ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          DELETE BRG
        </button>
        )}

        <button
          onClick={handleMembers}
          style={{
            padding: '9px 20px',
            backgroundColor: '#0078d4',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          MEMBERS
        </button>

        {isAdmin && (
        <button
          onClick={handleMasters}
          style={{
            padding: '9px 20px',
            backgroundColor: '#0078d4',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          MASTERS
        </button>
        )}

        {selectedBrgIds.length > 0 && (
          <span style={{ fontSize: '0.875rem', color: '#555', marginLeft: '8px' }}>
            {selectedBrgIds.length} selected
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* BRG Grid */}
        <div style={{ flex: 1, minWidth: '500px' }}>
          <BrgGrid
            brgs={sortedBrgs}
            onToggleSelect={toggleSelect}
            onBrgClick={(brgId) => navigate(`/brgs/${brgId}`)}
          />
        </div>

        {/* Engagement Card */}
        {engagement && (
          <div
            style={{
              width: '280px',
              flexShrink: 0,
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              padding: '20px 24px',
            }}
          >
            <h2 style={{ margin: '0 0 16px', fontSize: '1.1rem', color: '#1a1a1a' }}>
              Employee Engagement
            </h2>
            <div
              style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                color: '#0078d4',
                marginBottom: '4px',
              }}
            >
              {engagement.totalEngaged.toLocaleString()}
            </div>
            <p style={{ margin: '0 0 20px', fontSize: '0.85rem', color: '#666' }}>
              Total employees engaged
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <EngagementRow label="In 1 BRG" count={engagement.in1Brg} />
              <EngagementRow label="In 2 BRGs" count={engagement.in2Brgs} />
              <EngagementRow label="In 3 BRGs" count={engagement.in3Brgs} />
              <EngagementRow label="In 4 BRGs" count={engagement.in4Brgs} />
              <EngagementRow label="In 5+ BRGs" count={engagement.in5OrMoreBrgs} />
            </div>
          </div>
        )}
      </div>

      <DeleteBrgDialog
        brgName={selectedBrg?.name ?? ''}
        isOpen={deleteDialogOpen}
        isDeleting={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
}

function EngagementRow({ label, count }: { label: string; count: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '0.875rem', color: '#555' }}>{label}</span>
      <span
        style={{
          fontWeight: 600,
          fontSize: '0.95rem',
          color: '#1a1a1a',
          backgroundColor: '#f0f7ff',
          borderRadius: '12px',
          padding: '2px 10px',
          minWidth: '40px',
          textAlign: 'center',
        }}
      >
        {count.toLocaleString()}
      </span>
    </div>
  );
}

// BrgGrid Component using AG Grid
interface BrgGridProps {
  brgs: BrgListItemDto[];
  onToggleSelect: (id: number) => void;
  onBrgClick: (brgId: number) => void;
}

function BrgGrid({ brgs, onToggleSelect, onBrgClick }: BrgGridProps) {
  const gridRef = useRef<AgGridReact>(null);

  // Custom CSS for AG Grid styling (matching Members/Recognition style)
  const customGridStyles = `
    .ag-theme-quartz {
      --ag-header-background-color: #1a3bcc;
      --ag-header-foreground-color: #fff;
      --ag-header-cell-text-color: #fff;
      --ag-header-cell-padding: 8px 10px;
      --ag-header-cell-hover-background-color: #1530a8;
      --ag-header-cell-moving-background-color: #1530a8;
      --ag-font-size: 11px;
      --ag-cell-horizontal-padding: 10px;
      --ag-cell-vertical-padding: 8px;
      --ag-row-height: 32px;
      --ag-row-border-color: #f0f0f0;
      background-color: #ffffff;
      border: none;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-header {
      font-weight: 700;
      font-size: 11px;
      height: 32px;
      background-color: #1a3bcc;
    }
    
    .ag-theme-quartz .ag-header-cell {
      background-color: #1a3bcc !important;
      color: #fff !important;
      border-right: 1px solid #1530a8;
      font-weight: 700;
    }
    
    .ag-theme-quartz .ag-header-cell:last-child {
      border-right: none;
    }
    
    .ag-theme-quartz .ag-row {
      border-bottom: 1px solid #f0f0f0;
      height: 32px;
      background-color: #ffffff;
    }
    
    .ag-theme-quartz .ag-row:last-child {
      border-bottom: none;
    }
    
    .ag-theme-quartz .ag-row:hover {
      background-color: #f7f9ff !important;
    }
    
    .ag-theme-quartz .ag-cell {
      font-size: 11px;
      color: #333;
      padding: 8px 10px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
    
    .ag-theme-quartz .ag-root {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    }
    
    .ag-theme-quartz .ag-root-wrapper {
      border: none;
    }
    
    .ag-theme-quartz .ag-center-cols-viewport {
      overflow-x: auto;
    }
  `;

  interface BrgRow {
    brgId: number;
    status: BrgListItemDto['status'];
    name: string;
    memberCount: number;
    budget: number | null;
    spend: number | null;
  }

  const columnDefs: ColDef<BrgRow>[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 90,
        cellRenderer: (params: any) => renderStatusBadge(params.value as BrgListItemDto['status']),
      },
      {
        field: 'name',
        headerName: 'BRG Name',
        flex: 1.2,
        minWidth: 150,
        cellRenderer: (params: any) => (
          <span
            style={{
              color: '#0078d4',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            onClick={() => onBrgClick(params.data.brgId)}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: 'memberCount',
        headerName: 'Members',
        flex: 0.7,
        minWidth: 90,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params) => params.value?.toString() || '0',
      },
      {
        field: 'budget',
        headerName: 'Budget',
        flex: 0.8,
        minWidth: 100,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params) => formatCurrency(params.value || 0),
      },
      {
        field: 'spend',
        headerName: 'Spend',
        flex: 0.8,
        minWidth: 100,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (params) => formatCurrency(params.value || 0),
      },
    ],
    [onBrgClick]
  );

  const gridOptions: GridOptions<BrgRow> = {
    domLayout: 'normal',
    rowHeight: 32,
    headerHeight: 32,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: false,
    },
    suppressColumnVirtualisation: false,
    suppressCellFocus: true,
    suppressMovableColumns: true,
  };

  const rowData: BrgRow[] = useMemo(
    () =>
      brgs.map((brg) => ({
        brgId: brg.brgId,
        status: brg.status,
        name: brg.name,
        memberCount: brg.memberCount,
        budget: brg.budget || 0,
        spend: brg.spend || 0,
      })),
    [brgs]
  );

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        overflow: 'hidden',
        height: '500px',
      }}
    >
      <style>{customGridStyles}</style>
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact<BrgRow>
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          gridOptions={gridOptions}
          defaultColDef={{
            resizable: false,
            sortable: false,
            filter: false,
          }}
          onRowClicked={(event) => {
            // Handle row checkbox selection
            const brgId = event.data!.brgId;
            onToggleSelect(brgId);
          }}
          isExternalFilterPresent={() => false}
          doesExternalFilterPass={() => true}
          rowStyle={{
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
        />
      </div>
    </div>
  );
}
